import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

import apiUrl from '../config';
import LayoutDashboard from '../layout/LayoutDashboard';
import { NovaSectionLoader, NovaButtonLoader } from './ui/NovaLoader';
import '../CSS/Documents.css';

const standardTypes = ['Titre de Séjour', 'Carte Vitale', 'RIB', 'Contrat'];

const PageGestionDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);

  const [selectedUser, setSelectedUser] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const [openDialog, setOpenDialog] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [typeDocument, setTypeDocument] = useState('');
  const [otherType, setOtherType] = useState('');
  const [file, setFile] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [loadingUpload, setLoadingUpload] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('token');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` }
  });

  const getDecodedToken = () => {
    const token = getToken();
    if (!token) return null;

    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
      console.error('Token invalide:', error);
      return null;
    }
  };

  const normalizeRows = (data) => Array.isArray(data) ? data : [];

  const formatDate = (value) => {
    if (!value) return '—';

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
      return String(value);
    }

    return parsed.toLocaleDateString('fr-FR');
  };

  const getSelectedUserLabel = () => {
    if (!isAdmin) return 'Mes documents';

    if (selectedUser === 'all') return 'Tous les utilisateurs';

    const found = users.find((user) => String(user.id) === String(selectedUser));

    return found ? `${found.nom} ${found.prenom}` : 'Utilisateur sélectionné';
  };

  const stats = useMemo(() => {
    const total = documents.length;
    const types = new Set(documents.map((doc) => doc.type).filter(Boolean)).size;
    const contrats = documents.filter((doc) => String(doc.type || '').toLowerCase().includes('contrat')).length;
    const titres = documents.filter((doc) => String(doc.type || '').toLowerCase().includes('séjour') || String(doc.type || '').toLowerCase().includes('sejour')).length;

    return { total, types, contrats, titres };
  }, [documents]);

  const fetchUsers = useCallback(async () => {
    const response = await axios.get(`${apiUrl}/cdi-users`, getAuthHeaders());
    const validUsers = normalizeRows(response.data).filter((user) => user && user.id);
    setUsers(validUsers);
    return validUsers;
  }, []);

  const fetchDocuments = useCallback(async (userIdOverride = null, options = {}) => {
    const silent = Boolean(options.silent);
    const target = userIdOverride || selectedUser || currentUserId;

    if (!target) return;

    if (silent) {
      setRefreshing(true);
    } else {
      setLoadingDocuments(true);
    }

    try {
      const url = target === 'all'
        ? `${apiUrl}/documents/all`
        : `${apiUrl}/documents/${target}`;

      const response = await axios.get(url, getAuthHeaders());

      setDocuments(normalizeRows(response.data));
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);

      if (!silent) {
        setNotification({
          open: true,
          message: 'Erreur lors du chargement des documents.',
          severity: 'error'
        });
      }

      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
      setRefreshing(false);
    }
  }, [selectedUser, currentUserId]);

  useEffect(() => {
    const init = async () => {
      const token = getToken();

      if (!token) {
        navigate('/login');
        return;
      }

      const decodedToken = getDecodedToken();

      if (!decodedToken || decodedToken.exp < Date.now() / 1000) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      try {
        setLoadingDocuments(true);

        const admin = decodedToken.role?.toUpperCase() === 'ADMIN';
        const userId = decodedToken.user_id;
        const userIdFromProfile = location.state?.userId;

        setIsAdmin(admin);
        setCurrentUserId(userId);

        const initialSelected = admin ? (userIdFromProfile || 'all') : userId;

        setSelectedUser(initialSelected);

        if (admin) {
          await fetchUsers();
        }

        await fetchDocuments(initialSelected, { silent: false });
      } catch (error) {
        console.error('Erreur initialisation documents:', error);
        setNotification({
          open: true,
          message: 'Erreur lors du chargement de la page Documents.',
          severity: 'error'
        });
      } finally {
        setLoadingDocuments(false);
      }
    };

    init();
  }, [navigate, location.state?.userId, fetchUsers]);

  useEffect(() => {
    if (!currentUserId && !selectedUser) return;

    const interval = setInterval(() => {
      if (!openAddDialog && !openDialog && !loadingUpload && !deleting) {
        fetchDocuments(null, { silent: true });
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [currentUserId, selectedUser, openAddDialog, openDialog, loadingUpload, deleting, fetchDocuments]);

  const handleUserSelection = async (userId) => {
    setSelectedUser(userId);
    await fetchDocuments(userId, { silent: false });
  };

  const handleOpenAdd = () => {
    const decodedToken = getDecodedToken();
    const currentId = decodedToken?.user_id;

    if (isAdmin && (selectedUser === 'all' || !selectedUser)) {
      setNotification({
        open: true,
        message: 'Veuillez sélectionner un utilisateur avant d’ajouter un document.',
        severity: 'error'
      });
      return;
    }

    if (!isAdmin && !currentId) {
      setNotification({
        open: true,
        message: 'Utilisateur introuvable.',
        severity: 'error'
      });
      return;
    }

    setDocumentId(null);
    setTypeDocument('');
    setOtherType('');
    setFile(null);
    setOpenAddDialog(true);
  };

  const handleOpenEdit = (doc) => {
    setDocumentId(doc.id_document);

    if (standardTypes.includes(doc.type)) {
      setTypeDocument(doc.type);
      setOtherType('');
    } else {
      setTypeDocument('Autre');
      setOtherType(doc.type || '');
    }

    setFile(null);
    setOpenAddDialog(true);
  };

  const handleUploadDocument = async () => {
    const finalType = typeDocument === 'Autre' ? otherType.trim() : typeDocument;

    if (!finalType || (!documentId && !file)) {
      setNotification({
        open: true,
        message: documentId
          ? 'Veuillez renseigner le type du document.'
          : 'Veuillez remplir tous les champs.',
        severity: 'error'
      });
      return;
    }

    if (isAdmin && (selectedUser === 'all' || !selectedUser)) {
      setNotification({
        open: true,
        message: 'Veuillez sélectionner un utilisateur.',
        severity: 'error'
      });
      return;
    }

    setLoadingUpload(true);

    try {
      const decodedToken = getDecodedToken();

      const formData = new FormData();
      formData.append('type', finalType);
      formData.append('user_id', isAdmin ? selectedUser : decodedToken.user_id);

      if (file) {
        formData.append('document', file);
      }

      if (documentId) {
        await axios.put(`${apiUrl}/documents/${documentId}`, formData, getAuthHeaders());

        setNotification({
          open: true,
          message: 'Document modifié avec succès.',
          severity: 'success'
        });
      } else {
        await axios.post(`${apiUrl}/documents`, formData, getAuthHeaders());

        setNotification({
          open: true,
          message: 'Document ajouté avec succès.',
          severity: 'success'
        });
      }

      setOpenAddDialog(false);
      setDocumentId(null);
      setTypeDocument('');
      setOtherType('');
      setFile(null);

      await fetchDocuments(null, { silent: true });
    } catch (error) {
      console.error("Erreur lors de l'opération document:", error);
      setNotification({
        open: true,
        message: "Erreur lors de l'opération.",
        severity: 'error'
      });
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const response = await axios.get(`${apiUrl}/documents/download/${encodeURIComponent(filename)}`, {
        responseType: 'blob',
        ...getAuthHeaders()
      });

      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = urlBlob;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(urlBlob);
    } catch (error) {
      console.error('Erreur téléchargement document:', error);
      setNotification({
        open: true,
        message: 'Erreur lors du téléchargement du document.',
        severity: 'error'
      });
    }
  };

  const handleDelete = (docId) => {
    setDocToDelete(docId);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!docToDelete) return;

    setOpenDialog(false);
    setDeleting(true);

    try {
      await axios.delete(`${apiUrl}/documents/${docToDelete}`, getAuthHeaders());

      setDocuments((current) => current.filter((doc) => doc.id_document !== docToDelete));

      setNotification({
        open: true,
        message: 'Document supprimé avec succès.',
        severity: 'success'
      });

      setDocToDelete(null);

      await fetchDocuments(null, { silent: true });
    } catch (error) {
      console.error('Erreur suppression document:', error);
      setNotification({
        open: true,
        message: 'Erreur lors de la suppression du document.',
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <LayoutDashboard>
      <main className="documents-page">
        <section className="documents-hero">
          <div>
            <span className="documents-pill">Espace RH</span>
            <h1>Documents</h1>
            <p>
              Consultez, ajoutez, modifiez, téléchargez et supprimez les documents depuis un espace clair et rapide.
            </p>
          </div>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              className="documents-refresh-btn"
              variant="outlined"
              startIcon={refreshing ? null : <RefreshRoundedIcon />}
              disabled={refreshing}
              onClick={() => fetchDocuments(null, { silent: true })}
            >
              {refreshing ? 'Actualisation...' : 'Rafraîchir'}
            </Button>

            <Button
              className="documents-add-btn"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={handleOpenAdd}
            >
              Ajouter document
            </Button>
          </Stack>
        </section>

        <Container maxWidth={false} disableGutters className="documents-container-modern">
          <section className="documents-stats-grid">
            <article className="documents-stat-card">
              <span>Total</span>
              <strong>{stats.total}</strong>
              <small>document(s)</small>
            </article>

            <article className="documents-stat-card">
              <span>Types</span>
              <strong>{stats.types}</strong>
              <small>catégorie(s)</small>
            </article>

            <article className="documents-stat-card">
              <span>Contrats</span>
              <strong>{stats.contrats}</strong>
              <small>document(s)</small>
            </article>

            <article className="documents-stat-card">
              <span>Titres séjour</span>
              <strong>{stats.titres}</strong>
              <small>document(s)</small>
            </article>
          </section>

          <section className="documents-toolbar-card">
            <div>
              <Typography className="documents-section-title">Liste des documents</Typography>
              <Typography className="documents-section-helper">
                Vue active : {getSelectedUserLabel()}
              </Typography>
            </div>

            {isAdmin && (
              <TextField
                select
                label="Utilisateur"
                value={selectedUser || 'all'}
                onChange={(e) => handleUserSelection(e.target.value)}
                className="documents-user-select"
              >
                <MenuItem value="all">Tous les utilisateurs</MenuItem>

                {users.length > 0 ? (
                  users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.nom} {user.prenom}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>Aucun utilisateur trouvé</MenuItem>
                )}
              </TextField>
            )}
          </section>

          <section className="documents-table-card">
            {loadingDocuments ? (
              <NovaSectionLoader label="Chargement des documents" helper="Merci de patienter..." />
            ) : (
              <TableContainer className="documents-table-container">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nom</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Date de création</TableCell>
                      <TableCell align="center">Statut</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id_document}>
                        <TableCell>
                          <div className="documents-file-name">
                            <DescriptionRoundedIcon />
                            <span>{doc.nom || 'Document'}</span>
                          </div>
                        </TableCell>

                        <TableCell>
                          <Chip label={doc.type || 'Non renseigné'} className="documents-type-chip" size="small" />
                        </TableCell>

                        <TableCell>{formatDate(doc.date_de_creation)}</TableCell>

                        <TableCell align="center">
                          <Chip label="Disponible" className="documents-ok-chip" size="small" />
                        </TableCell>

                        <TableCell align="center">
                          <div className="documents-actions">
                            <Button
                              className="documents-action-download"
                              variant="outlined"
                              startIcon={<DownloadRoundedIcon />}
                              onClick={() => handleDownload(doc.nom)}
                            >
                              Télécharger
                            </Button>

                            <Button
                              className="documents-action-edit"
                              variant="outlined"
                              startIcon={<EditRoundedIcon />}
                              onClick={() => handleOpenEdit(doc)}
                            >
                              Modifier
                            </Button>

                            <Button
                              className="documents-action-delete"
                              variant="outlined"
                              startIcon={<DeleteRoundedIcon />}
                              onClick={() => handleDelete(doc.id_document)}
                            >
                              Supprimer
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}

                    {documents.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <div className="documents-empty-state">
                            <InsertDriveFileRoundedIcon />
                            <strong>Aucun document disponible</strong>
                            <span>Aucun document n’est disponible pour cette sélection.</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </section>
        </Container>

        <Dialog
          open={openDialog}
          onClose={() => !deleting && setOpenDialog(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Confirmation de suppression</DialogTitle>
          <DialogContent>
            <Typography className="documents-dialog-text">
              Êtes-vous sûr de vouloir supprimer ce document ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} variant="outlined" disabled={deleting}>
              Annuler
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              className="documents-confirm-delete"
              disabled={deleting}
            >
              {deleting ? <NovaButtonLoader label="Suppression" /> : 'Confirmer'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openAddDialog}
          onClose={() => !loadingUpload && setOpenAddDialog(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {documentId ? 'Modifier le document' : 'Ajouter un document'}
          </DialogTitle>

          <DialogContent>
            <Box className="documents-add-form">
              <TextField
                select
                label="Type de document"
                value={typeDocument}
                onChange={(e) => setTypeDocument(e.target.value)}
                fullWidth
              >
                <MenuItem value="Titre de Séjour">Titre de Séjour</MenuItem>
                <MenuItem value="Carte Vitale">Carte Vitale</MenuItem>
                <MenuItem value="RIB">RIB</MenuItem>
                <MenuItem value="Contrat">Contrat</MenuItem>
                <MenuItem value="Autre">Autre</MenuItem>
              </TextField>

              {typeDocument === 'Autre' && (
                <TextField
                  label="Précisez le type"
                  value={otherType}
                  onChange={(e) => setOtherType(e.target.value)}
                  fullWidth
                />
              )}

              <Button
                variant="outlined"
                component="label"
                className="documents-file-picker"
              >
                {documentId ? 'Choisir un nouveau fichier' : 'Choisir le fichier'}
                <input
                  type="file"
                  hidden
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>

              {file && (
                <div className="documents-selected-file">
                  <InsertDriveFileRoundedIcon />
                  <span>{file.name}</span>
                </div>
              )}

              {documentId && !file && (
                <Typography className="documents-edit-helper">
                  Vous pouvez modifier uniquement le type, ou choisir un nouveau fichier.
                </Typography>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setOpenAddDialog(false)}
              variant="outlined"
              disabled={loadingUpload}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={handleUploadDocument}
              disabled={loadingUpload}
            >
              {loadingUpload
                ? <NovaButtonLoader label="Enregistrement" />
                : documentId ? 'Valider modification' : 'Ajouter document'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={deleting ? null : 2800}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={notification.severity} className="documents-snackbar-alert">
            {notification.message}
          </Alert>
        </Snackbar>
      </main>
    </LayoutDashboard>
  );
};

export default PageGestionDocuments;
